import './notification-modal.scss';
import {Dialog, DialogTitle, Avatar, Box, DialogContent, Typography, useTheme} from '@mui/material';
import {Moment} from 'moment';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {AppContextType} from '../../contexts/app/types';
import {AppContext} from '../../contexts/app/context';
import {useContext} from 'react';
import {ThemeMode} from '../../enums/theme-mode';
import {avatarSizesAndCoefficients} from '../../utils/constants/avatar-sizes-and-coefficients';
import moment from 'moment';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';

/**
 * Generic notification modal component.
 */
export function NotificationModal(props: {
  title: string;
  avatarType: 'user' | 'entity';
  children: React.ReactNode;
  open: boolean;
  handleClose: () => void;
  avatarName?: string;
  avatarPicture?: string;
  time?: Date;
}) {
  // Get context
  const appContext: AppContextType = useContext(AppContext);

  // Get theme
  const theme = useTheme();

  // Translations
  const {t} = useTranslation();

  // Convert to local time
  let localTime: Moment | undefined;
  if (props.time) {
    // Get time since notification
    let utcTime: Moment = moment(props.time).utc();

    // Convert to local time of mobile phone
    localTime = moment(utcTime).local();
  }

  return (
    <Dialog scroll="paper" onClose={props.handleClose} open={props.open}>
      {/* ---- Title ---- */}
      <DialogTitle className="notification-modal-title">{props.title}</DialogTitle>
      <DialogContent>
        {/* ---- Avatar ---- */}
        <Avatar
          alt={props.avatarName}
          src={props.avatarPicture}
          sx={{
            width: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
            height: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
            borderRadius:
              props.avatarType === 'user'
                ? `${
                    avatarSizesAndCoefficients.avatarSizes.extraGiant *
                    avatarSizesAndCoefficients.borderRadiusCoefficients.circle
                  }px`
                : `${
                    avatarSizesAndCoefficients.avatarSizes.extraGiant *
                    avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersLow
                  }px`,
            margin: 'auto',
          }}
        >
          {props.avatarType === 'user' ? (
            <PersonIcon
              sx={{
                fontSize:
                  avatarSizesAndCoefficients.avatarSizes.extraGiant *
                  avatarSizesAndCoefficients.iconSizeCoefficient,
              }}
            />
          ) : (
            <BusinessIcon
              sx={{
                fontSize:
                  avatarSizesAndCoefficients.avatarSizes.extraGiant *
                  avatarSizesAndCoefficients.iconSizeCoefficient,
              }}
            />
          )}
        </Avatar>
        <Typography variant="h6" className="notification-modal-avatar-name">
          {props.avatarName}
        </Typography>
        {/* ---- Content ---- */}
        {props.children}
      </DialogContent>
      {/* ---- Datetime ---- */}
      <Box
        className="notification-modal-footer"
        sx={{
          bgcolor:
            appContext.state.currentThemeMode === ThemeMode.Light
              ? theme.palette.grey[100]
              : theme.palette.background.paper,
        }}
      >
        <Box className="notification-modal-footer-compartment">
          <Typography
            variant="overline"
            className="notification-modal-footer-compartment-text"
            sx={{
              color: theme.palette.primary.main,
            }}
          >
            {t(translations.notifications.date)}
          </Typography>
          <Typography variant="body1" className="notification-modal-footer-compartment-text">
            {localTime ? localTime.format('MM/DD/YYYY') : ''}
          </Typography>
        </Box>
        <Box className="notification-modal-footer-compartment">
          <Typography
            variant="overline"
            className="notification-modal-footer-compartment-text"
            sx={{
              color: theme.palette.primary.main,
            }}
          >
            {t(translations.notifications.time)}
          </Typography>
          <Typography variant="body1" className="notification-modal-footer-compartment-text">
            {localTime ? localTime.format('H:mm') : ''}
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
}
